import { useCallback, useEffect, useRef } from 'react'
import { useBillingApi } from '@/hooks/useApi/useBillingApi/useBillingApi'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useTranslation } from 'react-i18next'
import { processPayablesFilters } from '../utils/processPayablesFilters'

export const useExportPayablesReport = (reportId: string) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')

    const { useGetReport } = useReportsApi()
    const { data: report, refetch: refetchReport } = useGetReport(reportId, {
        enabled: false,
    })

    const { usePayablesSummaryQuery } = useBillingApi()

    const triggered = useRef(false)

    const filters = report ? processPayablesFilters(report.filters) : undefined

    const {
        data: payables,
        refetch: refetchPayables,
        downloadXlsx,
    } = usePayablesSummaryQuery(filters, {
        enabled: !!report && triggered.current,
    })

    const downloadData = useCallback(() => {
        void downloadXlsx().catch(() => {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        })
    }, [addAlert, downloadXlsx, t])

    useEffect(() => {
        if (triggered.current) {
            triggered.current = false
            downloadData()
        }
    }, [downloadData, payables])

    const exportPayablesReport = () => {
        triggered.current = true

        if (!report) {
            void refetchReport()
            return
        }

        if (!payables) {
            void refetchPayables()
            return
        }

        triggered.current = false
        downloadData()
    }

    return {
        exportPayablesReport,
    }
}
